import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Plus, User, Mail, Phone, Archive, ArchiveRestore, ShieldCheck, ShieldX, Trash2 } from "lucide-react";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CustomerFormModal } from "../components/admin/CustomerFormModal";
import { DeleteCustomerModal } from "../components/admin/DeleteCustomerModal";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function formatMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, archived: archivedFilter, limit: 200 });
      const res = await axios.get(`${API}/admin/customers?${params}`, { headers: headers() });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load customers");
    }
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(fetchCustomers, q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, archivedFilter]);

  const handleArchiveToggle = async (c) => {
    const action = c.archived ? "restore" : "archive";
    if (!window.confirm(`${action === "archive" ? "Archive" : "Restore"} ${c.name}?`)) return;
    try {
      await axios.post(`${API}/admin/customers/${c.customer_id}/${action}`, {}, { headers: headers() });
      toast.success(`Customer ${action === "archive" ? "archived" : "restored"}`);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  return (
    <AdminLayout
      title="Customers"
      subtitle={loading ? "Loading…" : `${total} ${archivedFilter === "active" ? "active" : archivedFilter === "archived" ? "archived" : "total"} customer${total === 1 ? "" : "s"}`}
      actions={
        <Button onClick={() => setShowCreate(true)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="create-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> New Customer
        </Button>
      }
    >
      {/* Toolbar */}
      <div className="bg-white border border-[#282828]/8 p-4 mb-6 flex flex-col sm:flex-row gap-3" data-testid="customers-toolbar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or phone (e.g. 04xx xxx xxx)"
            className="pl-10 border-[#282828]/10 text-[13px]"
            data-testid="customer-search"
          />
        </div>
        <div className="flex gap-1 text-[11px] uppercase tracking-wider">
          {["active", "archived", "all"].map(f => (
            <button
              key={f}
              onClick={() => setArchivedFilter(f)}
              className={`px-3 py-1.5 border ${archivedFilter === f ? "bg-[#282828] text-white border-[#282828]" : "border-[#282828]/10 text-[#676664]/60 hover:text-[#282828]"}`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="customers-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
        </div>
      ) : customers.length === 0 ? (
        <div className="bg-white border border-[#282828]/8 p-12 text-center text-[#676664]/50 text-sm" data-testid="customers-empty">
          {q ? "No customers match your search." : "No customers yet."}
        </div>
      ) : (
        <div className="bg-white border border-[#282828]/8 overflow-x-auto" data-testid="customers-table">
          <table className="w-full text-[12px]">
            <thead className="bg-[#FAF8F0] text-[#676664]/60 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Customer</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium">Verified</th>
                <th className="text-right px-4 py-3 font-medium">Bookings</th>
                <th className="text-right px-4 py-3 font-medium">Trials</th>
                <th className="text-right px-4 py-3 font-medium">Spend</th>
                <th className="text-left px-4 py-3 font-medium">Last Booking</th>
                <th className="text-left px-4 py-3 font-medium">Created</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#282828]/5">
              {customers.map((c) => (
                <tr
                  key={c.customer_id}
                  className={`hover:bg-[#FAF8F0]/50 cursor-pointer ${c.archived ? "opacity-50" : ""}`}
                  onClick={() => navigate(`/admin/customers/${c.customer_id}`)}
                  data-testid={`customer-row-${c.customer_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                      <span className="text-[#282828]">{c.name || "(no name)"}</span>
                      {c.archived && <span className="text-[9px] text-[#676664]/60 uppercase tracking-wider">archived</span>}
                    </div>
                    <div className="text-[10px] text-[#676664]/50 mt-0.5">{c.auth_provider || c.created_by || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-[#676664]/80">
                    <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-[#676664]/30" />{c.email}</div>
                    {c.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone className="w-3 h-3 text-[#676664]/30" />{c.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 text-[10px] ${c.email_verified ? "text-emerald-600" : "text-[#676664]/40"}`}>
                        {c.email_verified ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />} Email
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] ${c.phone_verified ? "text-emerald-600" : "text-[#676664]/40"}`}>
                        {c.phone_verified ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />} Phone
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-[#282828]">{c.total_bookings}</td>
                  <td className="px-4 py-3 text-right text-[#676664]/70">{c.total_trial_leads}</td>
                  <td className="px-4 py-3 text-right text-[#282828] font-medium">{formatMoney(c.total_spend)}</td>
                  <td className="px-4 py-3 text-[#676664]/60 text-[11px]">{formatDate(c.last_booking_at)}</td>
                  <td className="px-4 py-3 text-[#676664]/60 text-[11px]">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleArchiveToggle(c)}
                        className="text-[10px] text-[#676664]/50 hover:text-[#9A7B4F] inline-flex items-center gap-1"
                        data-testid={`archive-toggle-${c.customer_id}`}
                      >
                        {c.archived ? <><ArchiveRestore className="w-3 h-3" /> Restore</> : <><Archive className="w-3 h-3" /> Archive</>}
                      </button>
                      <button
                        onClick={() => setDeleting(c)}
                        className="text-[10px] text-red-500/70 hover:text-red-700 inline-flex items-center gap-1"
                        data-testid={`delete-customer-${c.customer_id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CustomerFormModal
          onClose={() => setShowCreate(false)}
          onSaved={(c) => { setShowCreate(false); fetchCustomers(); if (c?.customer_id) navigate(`/admin/customers/${c.customer_id}`); }}
        />
      )}

      {deleting && (
        <DeleteCustomerModal
          customer={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setCustomers((prev) => prev.filter((x) => x.customer_id !== deleting.customer_id));
          }}
        />
      )}
    </AdminLayout>
  );
}
